import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { colors, css, fonts, glyphs, useMediaQuery } from '../../Styles';
import { Button, ButtonList, DateField, InputField, Overlay, SelectField } from '..';
import { ButtonTypes, FieldStyles, FieldTypes } from '../../Definitions';

import { TestData as AnimalData, AnimalTypes } from '../../Assets/TestData/animalData';
import { AnimalGroupData as GroupData } from '../../Assets/TestData/animalGroups';
import { ConditionData } from '../../Assets/TestData/conditions';
import { LocationData } from '../../Assets/TestData/locations';

interface basicInfo_Props {
  selectedAnimal: number
}
/**
 * BasicInfo
 * @param selectedAnimal ID for the animal to display information about.
 * @returns JSX element displaying basic info about the selected animal.
 */
export default function BasicInfo({ selectedAnimal }:basicInfo_Props) {
  const {t} = useTranslation()
  const [editBasicData, setEditBasicData] = useState<boolean>(false)
  const [selectedType, setType] = useState<number>(0)
  const [selectedCondition, setCondition] = useState<number>(0)
  const [selectedGroup, setGroup] = useState<number>(0)
  const [selectedLocation, setLocation] = useState<number>(0)
  const [selectedBirthDate, setBirthDate] = useState<Date>(new Date());
  
  // #region | MediaQueries
  const colThree = useMediaQuery(``);
  const colTwo = useMediaQuery(``);
  const colOne = useMediaQuery(``);
  const mobileXs = useMediaQuery(`(max-width: 450px)`);
  const heightMax = useMediaQuery(`(max-height: 900px)`);
  // #endregion
  
  // #region | Data
  // Get animal from selectedAnimal
  const thisAnimal = useMemo(() => {
    return AnimalData.find(animal => animal.animalId === selectedAnimal);
  }, [selectedAnimal]);

  // Animal Type
  const typeOptions = Object.values(AnimalTypes).map(type => ({
    label: type
  }))

  // Animal condition
  const animalCondition = useMemo(() => {
    return ConditionData.find(condition => condition.conditionId === thisAnimal?.conditionId);
  }, [thisAnimal?.conditionId]);

  const conditionOptions = useMemo(() => {
  const options = ConditionData.map(con => ({
    label: con.conditionName,
    id: con.conditionId
  }));
  return options
  }, [] )

  // Animal group
  const animalGroup = useMemo(() => {
    return GroupData.find(group => group.groupId === thisAnimal?.groupId);
  }, [thisAnimal?.groupId]);
  
  const groupOptions = useMemo(() => {
    const options = GroupData.map(group => ({
      label: group.groupName,
      id: group.groupId
    }));
    return options
    }, [] )

  // Animal location
  const animalLocation = useMemo(() => {
    return LocationData.find(location => location.locationId === thisAnimal?.locationId);
  }, [thisAnimal?.locationId]);

  const locationOptions = useMemo(() => {
    const options = LocationData.map(location => ({
      label: location.locationName,
      id: location.locationId
    }));
    return options
    }, [] )

  // Get animal basic Info
  const type = thisAnimal && thisAnimal.animalType
  const group = thisAnimal && animalGroup?.groupName
  const location = thisAnimal && animalLocation?.locationName
  const condition = thisAnimal && animalCondition?.conditionName
  const conditionComponent = <div style={{padding: "4px 5px", ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: animalCondition?.conditionColorTag, borderRadius: "5px" }}>{condition}</div>
  const lifeNumber = thisAnimal && thisAnimal.lifeNo
  const responderNumber = thisAnimal && thisAnimal.responderNo
  const birthDate = thisAnimal && thisAnimal.birthDate
  // #endregion

  // #region | Content
  const basic_animalId = 
    <div style={{...css.basic_Info_style}}>
      <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("animalPages.animal.animalId")}</div>
      <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.AnimalNumber size='25px'/></div>
      <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editBasicData ? "5px" : "10px"}}>
        { editBasicData? 
          <InputField 
            inputType={"number"} 
            context={selectedAnimal?.toString()} 
            fieldType={FieldTypes.editField} 
            fieldStyle={FieldStyles.greySecondary}
          />
        : <>{selectedAnimal}</> }
      </div>
    </div>
  
  const basic_animalType =
    <div style={{...css.basic_Info_style}}>
      <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("animalPages.animal.animalType")}</div>
      <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.AnimalType size='25px'/></div>
      <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editBasicData ? "5px" : "10px"}}>
        { editBasicData
          ? <SelectField
              options={typeOptions}
              defaultValue={type}
              onChange={(e) => setType(parseInt(e[0].id + ''))}
              overrides={{
                fieldStyle: FieldStyles.greySecondary,
                fieldType: FieldTypes.editField,
                fieldWidth: mobileXs ? "136px" : "166px",
                dropdownWidth: "280px",
                dropdownLeft: mobileXs ? "-144px" : "-114px",
                dropdownBorderRadius: "10px 0px 10px 10px"
              }}
            />
          : <>{type}</> 
        }
      </div>
    </div>

  const basic_animalGroup = 
    <div style={{...css.basic_Info_style}}>
      <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("animalPages.animal.group")}</div>
      <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.AnimalGroup size='25px'/></div>
      <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editBasicData ? "5px" : "10px"}}>
        { editBasicData
          ? <SelectField
              options={groupOptions}
              defaultValue={group}
              overrides={{
                fieldStyle: FieldStyles.greySecondary,
                fieldType: FieldTypes.editField,
                fieldWidth: mobileXs ? "136px" : "166px",
                dropdownLeft: mobileXs ? "-144px" : "-114px",
                dropdownBorderRadius: "10px 0px 10px 10px"
              }}
              onChange={(e) => setGroup(parseInt(e[0].id + ''))}
            />
          : <>{group}</>
        }
      </div>
    </div>

  const basic_animalLocation =
    <div style={{...css.basic_Info_style}}>
      <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("animalPages.animal.location")}</div>
      <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.Location size='25px'/></div>
      <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editBasicData ? "5px" : "10px"}}>
        { editBasicData
          ? <SelectField
            options={locationOptions}
            defaultValue={location}
            overrides={{
              fieldStyle: FieldStyles.greySecondary,
              fieldType: FieldTypes.editField,
              fieldWidth: mobileXs ? "136px" : "166px",
              dropdownLeft: mobileXs ? "-144px" : "-114px",
              dropdownBorderRadius: "10px 0px 10px 10px"
            }}
            onChange={(e) => setLocation(parseInt(e[0].id + ''))}
          />
          : <>{location}</> 
        }
      </div>
    </div>
  
  const basic_animalCondition = 
    <div style={{...css.basic_Info_style}}>
      <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("animalPages.animal.condition")}</div>
      <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.Condition size='25px'/></div>
      <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editBasicData ? "5px" : "10px"}}>
        { editBasicData
          ? <SelectField
              options={conditionOptions}
              defaultValue={condition}
              overrides={{
                fieldStyle: FieldStyles.greySecondary,
                fieldType: FieldTypes.editField,
                fieldWidth: mobileXs ? "136px" : "166px",
                dropdownLeft: mobileXs ? "-144px" : "-114px",
                dropdownBorderRadius: "10px 0px 10px 10px"
              }}
              onChange={(e) => setCondition(parseInt(e[0].id + ''))}
            />
          : <>{conditionComponent}</> 
        }
      </div>
    </div>

const basic_animalLifeNo = 
  <div style={{...css.basic_Info_style}}>
    <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("animalPages.animal.lifeNo")}</div>
    <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.LifeNumber size='25px'/></div>
    <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editBasicData ? "5px" : "10px"}}>
      { editBasicData
        ? <InputField 
            inputType={"number"} 
            context={lifeNumber?.toString()} 
            fieldType={FieldTypes.editField} 
            fieldStyle={FieldStyles.greySecondary}
          />
        : <>{lifeNumber}</> 
      }
    </div>
  </div>

const basic_animalResponderNo = 
  <div style={{...css.basic_Info_style}}>
    <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("animalPages.animal.responderNo")}</div>
    <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.ResponderNumber size='25px'/></div>
    <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editBasicData ? "5px" : "10px"}}>
      { editBasicData
        ? <InputField 
            context={responderNumber?.toString()} 
            fieldType={FieldTypes.editField} 
            fieldStyle={FieldStyles.greySecondary}
          />
        : <>{responderNumber}</> 
      }
    </div>
  </div>

const basic_animalBirthDate = 
  <div style={{...css.basic_Info_style}}>
    <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("animalPages.animal.birthDate")}</div>
    <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.Animal size='25px'/></div>
    <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editBasicData ? "5px" : "10px"}}>
      { editBasicData
        ? <DateField 
            value={birthDate} 
            onChange={({ date }) => setBirthDate(date)}
            fieldType={FieldTypes.editField} 
            fieldStyle={FieldStyles.greySecondary}
          />
        : <>{birthDate ? new Intl.DateTimeFormat('en-EN', { day: '2-digit', month: '2-digit', year: 'numeric'}).format(birthDate) : ""}</> 
      }
    </div>
  </div>
  // #endregion

  return ( <>
    { editBasicData ? <Overlay color={colors.transparent}/> : <></> }
    <div style={{zIndex: editBasicData? "999" : "0", width: "100%", boxSizing: "border-box", position: "relative", height: "100%"}}>
      <div style={{width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "end", boxSizing: "border-box", padding: "0px 20px"}}>
        <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, marginBottom: "5px"}}>{t("animalPages.animal.basicInfo")}</p>

        { editBasicData
          ? <ButtonList 
              contents={[t("buttons.saveBtn"), t("buttons.cancelBtn"), t("buttons.edit")]} 
              onClick={(index: number) => {
                if (index === 0) {}
                if (index === 1) {setEditBasicData(false)}
                if (index === 2) {}
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[glyphs.Check({}), glyphs.Close({}), glyphs.Edit({})]}
              disabled={[false, false, true,]}
              overrides={(index) => ({
                baseButton: {
                  backgroundColor: index === 2 ? colors.greyPrimary : colors.greySecondary
                },
                disabledStyles: {
                  opacity: index === 2 ? 1 : 0.5
                }
              })}
            />
          : <Button content={t("buttons.edit")} startEnhancer={glyphs.Edit({})} onClick={() => setEditBasicData(true)} buttonType={ButtonTypes.tableButton}/>
        }
      </div>

      <div style={{overflowY: "scroll", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.greySecondary+colors.transparent, backgroundColor: colors.greyPrimary, padding: "20px", width: "100%", height: "calc(100% - 50px)", borderRadius: "10px", boxSizing: "border-box"}}>
        { !thisAnimal
          ? <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", ...fonts.fontSize3, ...fonts.paragraph, color: colors.greyLightTertiary}}>{t("animalPages.animal.noAnimalSelected")}</div>
          : <div style={{display: "flex", flexWrap: "wrap", margin: "0 auto", gap: "10px", 
              // width: colThree ? "990px" : colTwo ? "650px" : "320px"
              width: mobileXs ? "270px" : "320px"
            }}>
              {basic_animalId}{basic_animalType}
              {basic_animalLifeNo}{basic_animalResponderNo}
              {basic_animalCondition}{basic_animalGroup}
              {basic_animalLocation}{basic_animalBirthDate}
            </div>
        }
      </div>
    </div>
  </> );
}